class PreparedRouteRecord {
    _path = '';
    _action;
    _middleware = [];
    _name;
    _meta;
    _children = [];
    _attachedModifier;
    _props;
    constructor({ path, action, attachedModifier }) {
        this._attachedModifier = attachedModifier;
        if (!action.component && !action.components && !action.redirect)
            throw new Error('At least one action should be registered for route');
        this.path(path);
        this._action = action;
        if (attachedModifier?.middleware) {
            this._middleware = attachedModifier.middleware;
        }
        if (attachedModifier?.name) {
            this._name = attachedModifier.name;
        }
    }
    path(path) {
        if (this._attachedModifier)
            this._path = this._attachedModifier.prefix ? this._attachedModifier.prefix + path : path;
        else
            this._path = path;
        return this;
    }
    forcePath(path) {
        this._path = path;
        return this;
    }
    middleware(middleware) {
        if (!Array.isArray(middleware))
            middleware = [middleware];
        if (this._attachedModifier)
            this._middleware = this._attachedModifier.middleware ? this._attachedModifier.middleware.concat(middleware) : middleware;
        else
            this._middleware = middleware;
        return this;
    }
    forceMiddleware(middleware = []) {
        if (!Array.isArray(middleware))
            middleware = [middleware];
        this._middleware = middleware;
        return this;
    }
    clearMiddleware() {
        this.forceMiddleware();
    }
    name(name) {
        if (this._attachedModifier)
            this._name = this._attachedModifier.name ? this._attachedModifier.name + name : name;
        else
            this._name = name;
        return this;
    }
    forceName(name = undefined) {
        this._name = name;
        return this;
    }
    clearName() {
        this.forceName();
    }
    meta(meta) {
        this._meta = meta;
        return this;
    }
    addChild(child) {
        this._children.push(child);
        return this;
    }
    props(props) {
        this._props = props;
        return this;
    }
    toRawRoute() {
        const rawRoute = {
            path: this._path,
            name: this._name,
            meta: {
                middleware: this._middleware
            },
            children: this._children.map(child => child.toRawRoute()),
            props: this._props
        };
        if (this._action.component) {
            rawRoute.component = this._action.component;
        }
        else if (this._action.components) {
            rawRoute.components = this._action.components;
            return rawRoute;
        }
        if (this._action.redirect) {
            rawRoute.redirect = this._action.redirect;
            return rawRoute;
        }
        return rawRoute;
    }
}

async function executeMiddleware(middleware, context) {
    if ('handle' in middleware) {
        return await middleware.handle(context);
    }
    else {
        return await middleware(context);
    }
}
function nextFactory(context, middleware, index) {
    const subsequentMiddleware = middleware[index];
    if (!subsequentMiddleware)
        return async () => true;
    return async () => {
        const nextMiddleware = nextFactory(context, middleware, index + 1);
        return await executeMiddleware(subsequentMiddleware, { ...context, next: nextMiddleware });
    };
}
function registerMiddlewareProcessing(router) {
    router.beforeEach(async (to, from) => {
        if (to.meta.middleware) {
            const middleware = Array.isArray(to.meta.middleware)
                ? to.meta.middleware
                : [to.meta.middleware];
            if (middleware.length === 0)
                return void 0;
            const context = {
                to,
                from,
                router
            };
            const nextMiddleware = nextFactory(context, middleware, 1);
            const middlewareResult = await executeMiddleware(middleware[0], { ...context, next: nextMiddleware });
            // Let's check for the possible route location redirect
            // that can be returned from middleware chain.
            if (typeof middlewareResult !== 'boolean' && typeof middlewareResult !== 'undefined') {
                // And now we need to avoid the problem with the infinite redirect to
                // the same location, so we check if the redirect matches initial 'to' location name.
                if (typeof middlewareResult === 'string') {
                    if (middlewareResult !== to.path)
                        return middlewareResult;
                }
                else if ('path' in middlewareResult) {
                    if (middlewareResult.path !== to.path)
                        return middlewareResult;
                }
                else if ('name' in middlewareResult) {
                    if (middlewareResult.name !== to.name)
                        return middlewareResult;
                }
                return true;
            }
            return middlewareResult;
        }
        return void 0;
    });
    return router;
}

class RouteRegistrar {
    _routes = [];
    _modifiers = [];
    _resultModifierStates = [];
    _targets = [];
    get routes() {
        return this._routes;
    }
    clear() {
        this._routes = [];
        return this;
    }
    addRoute(path, action) {
        const lastState = this._resultModifierStates[this._resultModifierStates.length - 1];
        const newRouteRecord = new PreparedRouteRecord({
            attachedModifier: lastState,
            path: path,
            action: action
        });
        if (this._targets.length !== 0) {
            this._targets[this._targets.length - 1].addChild(newRouteRecord);
        }
        else {
            this._routes.push(newRouteRecord);
        }
        return newRouteRecord;
    }
    add(path, action) {
        return this.addRoute(path, action);
    }
    group(modifier, routesRegistrationClosure) {
        this.registerModifier(modifier);
        routesRegistrationClosure();
        this.dropLastModifier();
    }
    childrenGroup(path, { action, middleware, name, meta }, routesRegistrationClosure) {
        const newRoute = this.addRoute(path, action);
        if (middleware)
            newRoute.middleware(middleware);
        if (name)
            newRoute.name(name);
        if (meta)
            newRoute.meta(meta);
        this.registerModifier({ prefix: '' }, ['prefix']);
        this._targets.push(newRoute);
        routesRegistrationClosure();
        this._targets.pop();
        this.dropLastModifier();
        return newRoute;
    }
    registerModifier(modifier, overwriteFields = []) {
        if (modifier.middleware && !Array.isArray(modifier.middleware))
            modifier.middleware = [modifier.middleware];
        if (this._modifiers.length === 0) {
            if (modifier.prefix === undefined)
                modifier.prefix = '';
            if (modifier.name === undefined)
                modifier.name = '';
            if (modifier.middleware === undefined)
                modifier.middleware = [];
            this._modifiers.push(modifier);
            this._resultModifierStates.push(modifier);
            return this;
        }
        const lastState = { ...(this._resultModifierStates[this._resultModifierStates.length - 1]) };
        if (modifier.prefix !== undefined) {
            if (overwriteFields.includes('prefix'))
                lastState.prefix = modifier.prefix;
            else
                lastState.prefix += modifier.prefix;
        }
        if (modifier.name !== undefined) {
            if (overwriteFields.includes('name'))
                lastState.name = modifier.name;
            else
                lastState.name += modifier.name;
        }
        if (modifier.middleware) {
            if (overwriteFields.includes('middleware'))
                lastState.middleware = modifier.middleware;
            else
                lastState.middleware = lastState.middleware.concat(modifier.middleware);
        }
        this._modifiers.push(modifier);
        this._resultModifierStates.push(lastState);
        return this;
    }
    dropLastModifier() {
        this._modifiers.pop();
        this._resultModifierStates.pop();
        return this;
    }
    buildRoutes() {
        return this.routes.map(route => route.toRawRoute());
    }
}
const Route = new RouteRegistrar;

export { PreparedRouteRecord, Route, RouteRegistrar, registerMiddlewareProcessing };
